import { useEffect, useState, useRef } from "react";
import { fetchLessonExitQuiz } from "@/lib/api";
import type { ExitQuizApiResponse, LessonAttachment } from "@/types/api";
import type { LessonData } from "@/types/lesson";
import { FinishScreen } from "./FinishScreen";
import { PostLessonQuiz } from "./PostLessonQuiz";

interface LessonCompleteFlowProps {
  lesson: LessonData;
  courseId: number;
  lessonId: number;
  isChildTheme: boolean;
  onStartAgain: () => void;
  isAdminPreview?: boolean;
  /** All attachments (including videos) — shown on the finish screen. */
  attachments?: LessonAttachment[];
}

type Phase = "loading" | "quiz" | "finish";

function toApiQuiz(lesson: LessonData): ExitQuizApiResponse | null {
  const eq = lesson.exit_quiz;
  if (!eq?.questions?.length) return null;
  return {
    title: eq.title,
    questions: eq.questions.map((q, i) => ({
      id: typeof q.id === "number" ? Number(q.id) : i + 1,
      question: q.question,
      options: q.options.map((text, oi) => ({ id: oi + 1, text })),
    })),
  };
}

export function LessonCompleteFlow({
  lesson,
  courseId,
  lessonId,
  isChildTheme,
  onStartAgain,
  isAdminPreview,
  attachments = [],
}: LessonCompleteFlowProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [quiz, setQuiz] = useState<ExitQuizApiResponse | null>(null);
  const lessonRef = useRef(lesson);
  lessonRef.current = lesson;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = await fetchLessonExitQuiz(courseId, lessonId);
      if (cancelled) return;
      if (api?.questions?.length) {
        setQuiz(api);
        setPhase("quiz");
        return;
      }
      const embedded = toApiQuiz(lessonRef.current);
      if (embedded?.questions?.length) {
        setQuiz(embedded);
        setPhase("quiz");
        return;
      }
      setPhase("finish");
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, lessonId]);

  const adminBanner = isAdminPreview ? (
    <div
      className="w-full shrink-0 border-b border-amber-200/80 bg-amber-100/95 px-4 py-2 text-center text-xs font-semibold text-amber-950"
      role="status"
    >
      Admin preview — lesson progress and exit-quiz attempts are not saved.
    </div>
  ) : null;

  if (phase === "loading") {
    return (
      <div className="flex flex-1 flex-col min-h-0">
        {adminBanner}
        <div className="flex flex-1 flex-col items-center justify-center p-8 gap-3">
          <span className="inline-block h-8 w-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
          <p className="text-slate-600 text-sm">Preparing your wrap-up…</p>
        </div>
      </div>
    );
  }

  if (phase === "quiz" && quiz) {
    return (
      <div className="flex flex-1 flex-col min-h-0">
        {adminBanner}
        <PostLessonQuiz
          quiz={quiz}
          courseId={courseId}
          lessonId={lessonId}
          isChildTheme={isChildTheme}
          isAdminPreview={isAdminPreview === true}
          onContinue={() => setPhase("finish")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {adminBanner}
      <FinishScreen
        lesson={lesson}
        onStartAgain={onStartAgain}
        isChildTheme={isChildTheme}
        attachments={attachments}
      />
    </div>
  );
}
